/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { ApolloQueryResult } from 'apollo-client';
import { compose, setDisplayName, withProps } from 'recompose';

import { tupleToStringKey } from 'js/lib/stringKeyTuple';

import { Card, Grid, Typography2 } from '@coursera/cds-core';

import type {
  EnterpriseBadgeCollectionsQuery as EnterpriseBadgeCollectionsQueryData,
  EnterpriseBadgeCollectionsQueryVariables,
} from 'bundles/enterprise-learner-onboarding/queries/__generated__/EnterpriseBadgeCollectionsQuery';
import type { CircleMenuItem } from 'bundles/enterprise-legacy-learner-home/components/multiCourseProductCard/MultiCourseProductCirclesMenu';
import MultiCourseProductEnrolledCard from 'bundles/enterprise-legacy-learner-home/components/multiCourseProductCard/MultiCourseProductEnrolledCard';
import { removeCredentialFromBadgeTitle } from 'bundles/enterprise-legacy-learner-home/utils/EnterpriseBadgeUtils';
import { createContainer } from 'bundles/naptimejs';
// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import ProgramCurriculumProductsV1 from 'bundles/naptimejs/resources/programCurriculumProducts.v1';
import type { EnterpriseBadge } from 'bundles/page-config-common/providers/enterprise-skills/EnterpriseBadgeCollectionsProvider';

import _t from 'i18n!nls/course-cards';

const MIN_CARD_HEIGHT = 112;

/**
 * Max ids per Naptime multiGet request to avoid 414 Request-URL Too Large.
 * Each id is ~60–80 chars (programId~VerifiedCertificate~courseId); 20 keeps URL under ~2KB.
 */
const MAX_IDS_PER_PROGRAM_CURRICULUM_MULTIGET = 20;

function chunk<T>(array: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

const styles = {
  badgeCard: css`
    position: relative;
    background-color: var(--cds-color-neutral-background-primary);
    margin-bottom: var(--cds-spacing-200);
    overflow: hidden;
  `,
  issuedBanner: css`
    background-color: var(--cds-color-blue-50);
    padding: var(--cds-spacing-200);
    border-radius: 2px;
  `,
  card: css`
    min-height: ${MIN_CARD_HEIGHT}px;
    margin-bottom: var(--cds-spacing-200);
    padding: 0;
    margin-top: 2px;
    margin-left: 2px;
    margin-right: 2px;
    box-shadow: 0 1px 4px var(--cds-color-darken-200);
    border: 1px solid var(--cds-color-grey-25);
  `,
  cardEnterpriseProfileBar: css`
    min-height: ${MIN_CARD_HEIGHT}px;
    margin-bottom: var(--cds-spacing-200);
    box-shadow: none;
    gap: var(--cds-spacing-200);
    border-radius: var(--cds-border-radius-200);
    border: 1px solid var(--cds-color-grey-100);
    padding-top: var(--cds-spacing-200);
  `,
};

type PropsFromCaller = {
  programId: string;
  userId: number;
  enterpriseBadge: EnterpriseBadge;
  refetchEnterpriseBadgeCollections?: (
    variables?: EnterpriseBadgeCollectionsQueryVariables
  ) => Promise<ApolloQueryResult<EnterpriseBadgeCollectionsQueryData>>;
  isEnterpriseProfileBar?: boolean;
};

type PropsFromNaptime = {
  curriculumProducts?: Array<ProgramCurriculumProductsV1>;
};

type Props = PropsFromCaller & PropsFromNaptime;

export function BadgeCard({
  programId,
  userId,
  enterpriseBadge,
  curriculumProducts,
  refetchEnterpriseBadgeCollections,
  isEnterpriseProfileBar,
}: Props) {
  if (!enterpriseBadge || !curriculumProducts) {
    return null;
  }

  const collectionId = enterpriseBadge.collectionId;
  const badgeTitle = removeCredentialFromBadgeTitle(enterpriseBadge.title);
  const badgeProductIds = enterpriseBadge.productIds.map((productId) => productId.id);
  const activeEnrolledCourseIds: Array<string> = curriculumProducts
    .filter((session) => session.isEnrolled)
    .map((session) => session.courseId);

  const completedCourseIds = curriculumProducts
    .filter((product) => product.isCompleted)
    .map((product) => product.courseId);
  const selectedCourseId = badgeProductIds[0];
  const courseCount = badgeProductIds.length;

  const circleMenuData: CircleMenuItem[] = badgeProductIds.map((id, index) => ({
    courseId: id,
    isActive: selectedCourseId === id,
    isEnrolled: activeEnrolledCourseIds.includes(id),
    isCompleted: completedCourseIds.includes(id),
    index,
    isCapstone: false,
  }));

  return (
    <div>
      {enterpriseBadge.userState === 'ISSUED' && (
        <Grid container alignItems="center" css={styles.issuedBanner}>
          <Grid xs={12}>
            <Typography2 component="p" variant="bodySecondary">
              {_t("You've completed all courses in the #{title}.", {
                title: removeCredentialFromBadgeTitle(enterpriseBadge.title),
              })}
            </Typography2>
          </Grid>
        </Grid>
      )}
      <Card css={isEnterpriseProfileBar ? styles.cardEnterpriseProfileBar : styles.card}>
        <div css={styles.badgeCard} data-testid={`BadgeCard~${enterpriseBadge.badgeTemplateId}`}>
          {userId && (
            <MultiCourseProductEnrolledCard
              id={enterpriseBadge.badgeTemplateId}
              name={badgeTitle}
              logo={enterpriseBadge.imageUrl}
              description={enterpriseBadge.description || ''}
              programId={programId}
              collectionId={collectionId}
              userId={userId}
              itemType="BADGE"
              canUnenroll={true}
              courseCount={courseCount || 0}
              // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              firstCourseId={selectedCourseId}
              circleMenuData={circleMenuData}
              refetchEnterpriseBadgeCollections={refetchEnterpriseBadgeCollections}
            />
          )}
        </div>
      </Card>
    </div>
  );
}

const mergeChunkedCurriculumProducts = (props: {
  [key: string]: unknown;
}): { curriculumProducts: Array<ProgramCurriculumProductsV1> } => {
  const curriculumProducts = Object.values(props).flatMap((v) =>
    Array.isArray(v) ? (v as Array<ProgramCurriculumProductsV1>) : []
  );
  return { curriculumProducts };
};

const BadgeCardWithData = compose<Props, PropsFromCaller>(
  setDisplayName('BadgeCardWithData'),
  createContainer(({ enterpriseBadge, programId }: PropsFromCaller) => {
    const courseIds: Array<string> = enterpriseBadge.productIds.map((productId) => productId.id);
    const tupleCourseIds: Array<string> = courseIds.map((id) =>
      tupleToStringKey([programId, 'VerifiedCertificate', id])
    );
    const chunks = chunk(tupleCourseIds, MAX_IDS_PER_PROGRAM_CURRICULUM_MULTIGET);
    return chunks.reduce<Record<string, ReturnType<typeof ProgramCurriculumProductsV1.multiGet>>>((acc, ids, i) => {
      acc[`chunk${i}`] = ProgramCurriculumProductsV1.multiGet(ids, {});
      return acc;
    }, {});
  }),
  withProps(mergeChunkedCurriculumProducts)
)(BadgeCard);

export default BadgeCardWithData;
